<template>
  <main class="main-content flex-grow-1 p-4 overflow-auto mt-6" style="background-color: #f8f9fa">
    <h2 class="text-header-page" style="color: var(--Dark-100, #022326)">
      Balance
    </h2>
    <!-- Sandbox Mode Alert -->
    <div v-if="environment === 'sandbox'" class="alert alert-warning mb-4 text-white" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      You are currently on sandbox mode, where all operations are testing
      purpose only and do not affect live data
    </div>

    <div class="row justify-content-center">
      <div class="col-12 px-0">
        <!-- Balance Card Container -->
        <div class="card card-custom m-4">
          <div class="card-body p-0">
            <BalanceCard :balance="balance" :loading="balanceLoading" @add-balance="showAddBalance = true" />
          </div>
        </div>
        <!-- Activity Table Container -->
        <div class="card card-custom m-4">
          <div class="card-body">
            <div class="activity-section p-0">
              <div class="activity-label">Activity</div>
              <BalanceActivityTable ref="balanceActivityTable" :activities="activities" :loading="activitiesLoading"
                :pagination-data="pagination" :initial-filters="initialFilters" @page-change="handlePageChange"
                @export-csv="handleExportCSV" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <AddBalanceModal :show="showAddBalance" @close="showAddBalance = false" />
  </main>
</template>

<script>
import BalanceCard from "./BalanceCard.vue";
import BalanceActivityTable from "./BalanceActivityTable.vue";
import AddBalanceModal from "./AddBalanceModal.vue";
import { getBalance, historyBalance } from "../../api/balance";

export default {
  name: "BalanceView",
  computed: {
    environment() {
      return this.$store.getters.currentEnvironment;
    },
  },
  components: {
    BalanceCard,
    BalanceActivityTable,
    AddBalanceModal,
  },
  data() {
    const balanceValue = 0;
    const activitiesData = [];
    return {
      balance: balanceValue,
      activities: activitiesData,
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null
      },
      showAddBalance: false,
      balanceLoading: false,
      activitiesLoading: false,
      initialFilters: {},
    };
  },
  methods: {
    async fetchBalance() {
      this.balanceLoading = true;
      try {
        const response = await getBalance();
        if (response && response.status === "success" && response.data && response.data.merchant) {
          this.balance = response.data.merchant.balance || 0;
        } else {
          console.warn('Balance API response structure unexpected:', response);
          this.balance = 0;
        }
      } catch (error) {
        console.error('Error fetching balance:', error);
        this.balance = 0;
      } finally {
        this.balanceLoading = false;
      }
    },
    async fetchHistoryBalance(page = 1, perPage = 20, filters = {}) {
      this.activitiesLoading = true;
      try {
        // Prepare pagination parameters
        const params = {
          page: page,
          per_page: perPage
        };

        // Add filter parameters if they exist
        if (filters.search && filters.search.trim() !== "") {
          params.ref_id = filters.search.trim();
        }

        if (filters.status && filters.status.trim() !== "") {
          params.source = filters.status.trim();
        }

        if (filters.dateRange && Array.isArray(filters.dateRange) && filters.dateRange.length === 2 && filters.dateRange[0] && filters.dateRange[1]) {
          const startDate = this.formatDateToAPI(new Date(filters.dateRange[0]));
          const endDate = this.formatDateToAPI(new Date(filters.dateRange[1]));
          params.date_range = `${startDate} - ${endDate}`;
        }

        const response = await historyBalance(params);
        if (response && response.status === "success" && response.data) {
          // Update activities data
          if (response.data.data) {
            this.activities = response.data.data;
            // Debug: Log first item to see structure
            if (this.activities.length > 0) {
              console.log('Sample balance activity item:', this.activities[0]);
            }
          } else {
            console.warn('History balance API response missing data array:', response);
            this.activities = [];
          }

          // Update pagination data if available
          if (response.data.pagination) {
            this.pagination = response.data.pagination;
          }
        } else {
          console.warn('History balance API response structure unexpected:', response);
          this.activities = [];
        }
      } catch (error) {
        console.error('Error fetching balance history:', error);
        this.activities = [];
      } finally {
        this.activitiesLoading = false;
      }
    },

    // Handle page change from child component
    handlePageChange(page, perPage, filters = {}) {
      this.fetchHistoryBalance(page, perPage, filters);
    },

    // Format date to API format (YYYY-MM-DD)
    formatDateToAPI(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${day}/${month}/${year}`;
    },

    // Handle export CSV with filters
    async handleExportCSV(filters) {
      try {
        // Show loading state (optional)
        this.activitiesLoading = true;

        // Fetch all data with filters applied
        const allFilteredData = await this.fetchAllFilteredData(filters);

        // Use the child component's downloadCSV method
        if (this.$refs.balanceActivityTable) {
          this.$refs.balanceActivityTable.downloadCSV(allFilteredData);
        }
      } catch (error) {
        console.error('Error exporting CSV:', error);
        alert('Error exporting data. Please try again.');
      } finally {
        this.activitiesLoading = false;
      }
    },

    // Fetch all data with filters applied
    async fetchAllFilteredData(filters) {
      try {
        // Prepare parameters for API call
        const params = {
          page: 1,
          per_page: 10000, // Large number to get all data
        };

        // Add filter parameters using the same format as fetchHistoryBalance
        if (filters.search && filters.search.trim() !== "") {
          params.ref_id = filters.search.trim();
        }

        if (filters.status && filters.status.trim() !== "") {
          params.source = filters.status.trim();
        }

        if (filters.dateRange && Array.isArray(filters.dateRange) && filters.dateRange.length === 2 && filters.dateRange[0] && filters.dateRange[1]) {
          const startDate = this.formatDateToAPI(new Date(filters.dateRange[0]));
          const endDate = this.formatDateToAPI(new Date(filters.dateRange[1]));
          params.date_range = `${startDate} - ${endDate}`;
        }

        const response = await historyBalance(params);

        if (response && response.status === "success" && response.data && response.data.data) {
          return response.data.data;
        } else {
          console.warn('Export API response structure unexpected:', response);
          return [];
        }
      } catch (error) {
        console.error('Error fetching filtered data for export:', error);
        throw error;
      }
    },
    stripTime(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },

    // Extract URL parameters for initial filtering
    extractUrlParameters() {
      const query = this.$route.query;
      const filters = {};

      if (query.ref_id) {
        filters.search = query.ref_id;
      }

      if (query.source) {
        filters.status = query.source;
      }

      if (query.start_date && query.end_date) {
        filters.dateRange = [
          new Date(query.start_date),
          new Date(query.end_date)
        ];
      }

      return filters;
    },
  },
  mounted() {
    // Extract URL parameters for initial filtering
    this.initialFilters = this.extractUrlParameters();

    this.fetchBalance();
    // The BalanceActivityTable child component will handle the initial history fetch
    // with its default date filter (today) or with URL parameters if provided
  },
};
</script>

<style scoped>
.activity-label {
  color: #121313;
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 15px;
  /* atas kanan bawah kiri */
}

.title {
  font-weight: bold;
  font-size: 1.75rem;
  letter-spacing: -0.5px;
  color: #111827;
  margin: 0 0 0 15px;
}
</style>
